import { Box, TablePagination } from "@mui/material";
import { useState, ReactNode, ReactElement, JSXElementConstructor } from "react";
import { StyledDatagrid } from "@/components/ui/react-admin/StyledDatagrid";
import { useTablePagination } from "@/hooks/useTablePagination";
import { ValidAny } from "@/types";

interface PaginatedDatagridProps {
  data: ValidAny[];
  isLoading?: boolean;
  enablePagination?: boolean;
  initialPerPage?: number;
  bulkActionButtons?: false | ReactElement<ValidAny, string | JSXElementConstructor<ValidAny>>;
  isRowSelectable?: (record: ValidAny) => boolean;
  rowClick?: string | ((id: string | number) => string | false) | false;
  onRowClick?: (record: ValidAny) => void;
  sort?: { field: string; order: "ASC" | "DESC" };
  empty?: ReactElement<ValidAny, string | JSXElementConstructor<ValidAny>>;
  expand?: ReactElement<ValidAny, string | JSXElementConstructor<ValidAny>>;
  fixedLayout?: boolean;
  resource?: string;
  children: ReactNode;

  // Server-side pagination props (optional)
  serverSide?: boolean;
  totalCount?: number;
  page?: number;
  onPageChange?: (newPage: number) => void;
  rowsPerPage?: number;
  onRowsPerPageChange?: (newRowsPerPage: number) => void;
}

/**
 * PaginatedDatagrid - A wrapper around StyledDatagrid with built-in pagination
 *
 * Supports both client-side and server-side pagination modes.
 *
 * **Client-side mode (default):**
 * Uses the shared `useTablePagination` hook internally. All data is loaded in
 * memory and the component handles slicing and page state automatically.
 *
 * **Server-side mode:**
 * Use when data needs to be fetched page by page. Set `serverSide={true}` and provide
 * `totalCount`, `page`, `onPageChange` props. The parent component controls pagination.
 *
 * @example Client-side pagination
 * ```tsx
 * <PaginatedDatagrid
 *   data={documents}
 *   isLoading={loading}
 *   rowClick={(id) => `/documents/${id}/show`}
 *   initialPerPage={25}
 * >
 *   <TextField source="name" />
 * </PaginatedDatagrid>
 * ```
 *
 * @example Server-side pagination
 * ```tsx
 * const [page, setPage] = useState(0);
 * const [perPage, setPerPage] = useState(25);
 *
 * <PaginatedDatagrid
 *   data={currentPageData}
 *   serverSide
 *   totalCount={totalRecords}
 *   page={page}
 *   onPageChange={setPage}
 *   rowsPerPage={perPage}
 *   onRowsPerPageChange={setPerPage}
 * >
 *   <TextField source="name" />
 * </PaginatedDatagrid>
 * ```
 */
export const PaginatedDatagrid = ({
  data,
  isLoading = false,
  enablePagination = true,
  initialPerPage = 25,
  bulkActionButtons = false,
  isRowSelectable,
  rowClick,
  onRowClick,
  sort,
  empty,
  expand,
  resource,
  fixedLayout,
  children,
  // Server-side pagination props
  serverSide = false,
  totalCount,
  page: externalPage,
  onPageChange: externalOnPageChange,
  rowsPerPage: externalRowsPerPage,
  onRowsPerPageChange: externalOnRowsPerPageChange,
}: PaginatedDatagridProps) => {
  // ==================== CLIENT-SIDE PAGINATION ====================
  // Shared hook handles slicing, page reset on data change, and MUI handlers

  const clientPagination = useTablePagination(data, {
    defaultRowsPerPage: initialPerPage,
    rowsPerPageOptions: [10, 25, 50, 100],
  });

  // ==================== SERVER-SIDE PAGINATION ====================

  const [serverPage, setServerPage] = useState(0);
  const [serverPerPage, setServerPerPage] = useState(initialPerPage);

  const handleServerPageChange = (_event: unknown, newPage: number) => {
    if (externalOnPageChange) {
      externalOnPageChange(newPage);
    } else {
      setServerPage(newPage);
    }
  };

  const handleServerRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPerPage = parseInt(event.target.value, 10);
    if (externalOnRowsPerPageChange) {
      externalOnRowsPerPageChange(newPerPage);
    }
    if (externalOnPageChange) {
      externalOnPageChange(0);
    } else {
      setServerPerPage(newPerPage);
      setServerPage(0);
    }
  };

  // ==================== RESOLVE MODE ====================

  const isClientSide = !serverSide && enablePagination;

  const displayData = isClientSide ? clientPagination.paginatedRows : data;

  const page = serverSide ? (externalPage ?? serverPage) : clientPagination.page;
  const perPage = serverSide ? (externalRowsPerPage ?? serverPerPage) : clientPagination.rowsPerPage;
  const totalRecords = serverSide ? (totalCount ?? data.length) : clientPagination.totalCount;
  const showPagination = enablePagination && (isClientSide ? clientPagination.showPagination : totalRecords > 0);

  const handlePageChange = isClientSide ? clientPagination.handlePageChange : handleServerPageChange;
  const handleRowsPerPageChange = isClientSide
    ? clientPagination.handleRowsPerPageChange
    : handleServerRowsPerPageChange;

  // ==================== RENDER ====================

  return (
    <Box>
      <StyledDatagrid
        data={displayData}
        total={displayData.length}
        bulkActionButtons={bulkActionButtons}
        isRowSelectable={isRowSelectable}
        rowClick={
          onRowClick
            ? (_id, _resource, record) => {
                onRowClick(record);
                return false;
              }
            : rowClick
        }
        isLoading={isLoading}
        sort={sort}
        empty={empty}
        expand={expand}
        resource={resource}
        fixedLayout={fixedLayout}
      >
        {children}
      </StyledDatagrid>

      {showPagination && (
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={perPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}
    </Box>
  );
};
